import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { SystemState, SystemStats, SystemHealth } from '../../types';
import { systemApi } from '../../services/systemApi';

const initialState: SystemState & { notifications?: any[] } = {
  stats: null,
  health: null,
  isLoading: false,
  error: null,
  notifications: [],
};

export const fetchSystemStats = createAsyncThunk(
  'system/fetchSystemStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await systemApi.getSystemStats();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch system stats');
    }
  }
);

export const fetchSystemHealth = createAsyncThunk(
  'system/fetchSystemHealth',
  async (_, { rejectWithValue }) => {
    try {
      const response = await systemApi.getSystemHealth();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch system health');
    }
  }
);

// Notifications
export const fetchNotifications = createAsyncThunk('system/fetchNotifications', async (_, { rejectWithValue }) => {
  try {
    const res = await systemApi.getNotifications();
    return res;
  } catch (e: any) {
    return rejectWithValue(e.message || 'Failed to load notifications');
  }
});
export const markNotificationRead = createAsyncThunk('system/markNotificationRead', async (id: string, { rejectWithValue }) => {
  try {
    await systemApi.markNotificationRead(id);
    return id;
  } catch (e: any) {
    return rejectWithValue(e.message || 'Failed to mark as read');
  }
});
export const markAllNotificationsRead = createAsyncThunk('system/markAllNotificationsRead', async () => {
  await systemApi.markAllRead();
  return true;
});

const systemSlice = createSlice({
  name: 'system',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch system stats
      .addCase(fetchSystemStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSystemStats.fulfilled, (state, action: PayloadAction<SystemStats>) => {
        state.isLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchSystemStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch system health
      .addCase(fetchSystemHealth.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSystemHealth.fulfilled, (state, action: PayloadAction<SystemHealth>) => {
        state.isLoading = false;
        state.health = action.payload;
      })
      .addCase(fetchSystemHealth.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Notifications handlers
      .addCase(fetchNotifications.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.isLoading = false;
        (state as any).notifications = action.payload;
      })
      .addCase(fetchNotifications.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        (state as any).notifications = (state as any).notifications.map((n: any) => n.id === action.payload ? { ...n, read: true } : n);
      })
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        (state as any).notifications = (state as any).notifications.map((n: any) => ({ ...n, read: true }));
      });
  },
});

export const { clearError } = systemSlice.actions;
export default systemSlice.reducer;
